import React, { useMemo } from 'react';
import { eur, eurCourt, montant, pct1, useFiltresVentes, PanneauVentes } from './ventesUtils';

// ─── Fournisseurs & charges — utilitaires partagés (Performance + Analyse) ──
// Mêmes filtres que les rapports VN/VO (période YTD / 12 mois / mois précis,
// sociétés) + la catégorie de charges (achats marchandises / frais généraux /
// autres), tous portés par l'URL.

export const CATS = [
  { code: 'cogs', nom: 'Achats marchandises', couleur: '#2E5BFF' },
  { code: 'fg', nom: 'Frais généraux', couleur: '#0F766E' },
  { code: 'autres', nom: 'Autres', couleur: '#c9d0d9' },
];
export const DIMS = [
  { code: 'fournisseur', label: 'Fournisseur', un: 'fournisseur', sous: 'compte' },
  { code: 'regroupement', label: 'Regroupement', un: 'regroupement', sous: 'fournisseur' },
  { code: 'service', label: 'Service', un: 'service', sous: 'fournisseur' },
  { code: 'type', label: 'Type de coût', un: 'type de coût', sous: 'fournisseur' },
  { code: 'compte', label: 'Compte', un: 'compte', sous: 'fournisseur' },
  { code: 'societe', label: 'Société', un: 'société', sous: 'fournisseur' },
];
export const dimObj = (code) => DIMS.find((d) => d.code === code) || DIMS[0];

// Écart N-1 d'une charge : la HAUSSE est en rouge, la baisse en vert (lecture
// « coût »). Sans N-1 : tiret. Le Δ % n'est pas calculé sous 1 k€ de base N-1
// (un +52 000 % sur 3 € n'apprend rien et polluerait les tris).
export const BASE_MIN = 1000;
export const deltaPct = (v, n1) => (n1 && Math.abs(n1) >= BASE_MIN ? (v - n1) / Math.abs(n1) * 100 : null);
// `unite` optionnelle : sans elle, le comportement d'origine (abrégé) est
// conservé pour les autres écrans Fournisseurs, qui n'ont pas de sélecteur.
export const signeEur = (v, unite) => (v > 0 ? '+' : '') + montant(v, unite);
export const signePct1 = (v) => (v == null ? '—' : (v > 0 ? '+' : '') + pct1(v));
export const Delta = ({ valeur, n1, mode = 'eur', as = 'td', className = '' }) => {
  const Tag = as;
  if (!n1) return <Tag className={`fr-d ${className}`}>—</Tag>;
  const d = valeur - n1;
  if (mode === 'pct' && Math.abs(n1) < BASE_MIN) return <Tag className={`fr-d ${className}`} title="Base N-1 inférieure à 1 k€">—</Tag>;
  const txt = mode === 'pct' ? signePct1(deltaPct(valeur, n1)) : signeEur(d);
  return <Tag className={`fr-d ${d > 0 ? 'up' : d < 0 ? 'down' : ''} ${className}`}>{txt}</Tag>;
};

// Hook filtres : ceux des ventes + la catégorie.
export function useFiltresFourn(societesDispo) {
  const f = useFiltresVentes(societesDispo);
  const catsUrl = f.params.get('cats') || '';
  const catsActives = useMemo(() => (catsUrl ? catsUrl.split(',').filter(Boolean) : CATS.map((c) => c.code)), [catsUrl]);
  const toggleCategorie = (code) => {
    const set = new Set(catsActives); set.has(code) ? set.delete(code) : set.add(code);
    const l = [...set]; f.setFiltre({ cats: l.length === 0 || l.length === CATS.length ? '' : l.join(',') });
  };
  const query = { periode: f.periode, mois: f.moisStr, societes: f.societesUrl, cats: catsUrl };
  const cle = `${f.periode}|${f.moisStr}|${f.societesUrl}|${catsUrl}`;
  return { ...f, catsUrl, catsActives, toggleCategorie, query, cle, nbActifs: f.nbActifs + (catsUrl ? 1 : 0) };
}

export function PanneauFourn({ ouvert, onToggle, f, societesDispo }) {
  const ff = { ...f, setFiltre: (c) => f.setFiltre(c.societes === '' && c.periode === '' && c.mois === '' ? { ...c, cats: '' } : c) };
  return <PanneauVentes ouvert={ouvert} onToggle={onToggle} f={ff} societesDispo={societesDispo}
    extra={{ categoriesDispo: CATS, categoriesActives: f.catsActives, onToggleCategorie: f.toggleCategorie, titreCategories: 'Catégorie de charges' }} />;
}

// Avertissement : mois en cours de saisie. La réserve de couverture (« SOGUAVA
// et SOREC seulement ») est tombée avec le passage au CSV quotidien des six
// entités (2026-09-29).
export function Avertissements({ data }) {
  if (!data?.partiel) return null;
  return (
    <div className="fr-warn">⚠️ Le mois de <b>{data.partiel.label}</b> est en cours de saisie ({data.partiel.nb} écritures) : il est exclu des cumuls et des comparaisons N-1, sauf sélection explicite.</div>
  );
}

// Tendance mensuelle miniature (série N).
export function Sparkline({ serie, largeur = 84, hauteur = 20 }) {
  if (!serie || serie.length < 2) return null;
  const max = Math.max(...serie.map((v) => Math.abs(v)), 1);
  const pas = largeur / (serie.length - 1);
  const pts = serie.map((v, i) => `${(i * pas).toFixed(1)},${(hauteur - 2 - Math.max(0, v) / max * (hauteur - 4)).toFixed(1)}`).join(' ');
  return (
    <svg className="fr-spark" width={largeur} height={hauteur} viewBox={`0 0 ${largeur} ${hauteur}`} aria-hidden="true">
      <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <circle cx={(serie.length - 1) * pas} cy={hauteur - 2 - Math.max(0, serie[serie.length - 1]) / max * (hauteur - 4)} r="1.8" fill="currentColor" />
    </svg>
  );
}

// Modale des écritures d'un fournisseur (période N, ≤ 500 lignes récentes).
export function ModaleEcritures({ drill, onFermer }) {
  if (!drill) return null;
  return (
    <div className="fr-modal-ov" onClick={onFermer}>
      <div className="fr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fr-modal-h">
          <b>{drill.nom}</b>
          <span>{drill.lignes ? `${drill.periode || ''} · ${eurCourt(drill.montant)} HT · ${drill.nb} écriture(s)${drill.affichees < drill.nb ? ` (${drill.affichees} récentes affichées)` : ''}` : ''}</span>
          <button className="fr-modal-x" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        <div className="fr-modal-b">
          {!drill.lignes ? <div className="fr-empty-tbl">Chargement…</div> : drill.lignes.length === 0 ? (
            <div className="fr-empty-tbl">{drill.erreur || 'Aucune écriture.'}</div>
          ) : (
            <table className="fr-tbl fr-tbl-detail">
              <thead><tr><th>Date</th><th>Société</th><th>Pièce</th><th>Libellé</th><th>Compte</th><th>Service</th><th>Type de coût</th><th className="num">Montant HT</th></tr></thead>
              <tbody>
                {drill.lignes.map((l, i) => (
                  <tr key={i}>
                    <td>{l.date ? l.date.split('-').reverse().join('/') : '—'}</td>
                    <td>{l.societe}</td>
                    <td className="cd">{l.piece}</td>
                    <td className="el" title={l.libelle || ''}>{l.libelle || '—'}</td>
                    <td className="cd el" title={l.compte || ''}>{l.compte || '—'}</td>
                    <td className="el">{l.service || '—'}</td>
                    <td className="el" title={l.type || ''}>{l.type || '—'}</td>
                    <td className="num"><b>{eur(l.montant)}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
